/*
  AUTOID mega menu motion (desktop, 996px and wider). The styles are in blocks/_header-menu.liquid,
  section "Mega menu motion"; docs/MEGA-MENU.md has the overview.

  assets/header-menu.js (Horizon) still decides which panel is open and writes aria-expanded on each
  item's disclosure button. This script only watches that state and adds four things on top:

  - One glass sheet for the three AUTOID panels (the "stage"). The panels lose their own glass and sit on
    it, so moving from one menu to the next keeps the sheet in place: its height eases to the new panel and
    the content slides a little in the direction the pointer moved.
  - A sliding underline under the open or hovered top-level item, in place of one underline per item.
  - A soft dim over the page while a panel is open.
  - aria-current on panel links to the page being viewed, so the stylesheet can set them in bold.

  With this script missing, each panel keeps its own glass and opens as before.
*/
(() => {
  if (window.__autoidMegaMotion) return;
  window.__autoidMegaMotion = true;

  const DESKTOP = window.matchMedia('(min-width: 996px)');

  /* Moving from one item to the next, Horizon closes the first panel a moment before it opens the next
     (pointerleave, then pointerenter, sometimes a frame or two apart over the gap between items). The sheet
     waits this long before it closes, so a move between items reads as one switch, not close then open. */
  const CLOSE_GRACE_MS = 120;

  /* Rows that fade in one after another when a panel opens; past this many they arrive together. */
  const STAGGER_MAX = 14;
  const STAGGER_ROWS = [
    '.autoid-product__list > li',
    '.autoid-brand__rows > li',
    '.autoid-car__list > li',
    '.autoid-mega--car[data-layout="compact"] .autoid-car__col--fitment > *',
  ];

  const TOP_ITEM = '.menu-list__list-item:not([slot])';
  const EXPANDED = ':scope > .menu-list__trigger > [aria-expanded="true"]';

  function init() {
    const menu = document.querySelector('header-menu');
    const nav = menu?.querySelector('.menu-list');
    const firstInner = menu?.querySelector('.menu-list__submenu-inner--autoid[data-autoid-chrome="true"]');
    if (!menu || !nav || !firstInner) return;

    const stage = document.createElement('div');
    stage.className = 'autoid-mp-stage';
    stage.setAttribute('aria-hidden', 'true');
    stage.dataset.autoidSurface = firstInner.dataset.autoidSurface || 'frosted';
    const glass = document.createElement('div');
    glass.className = 'autoid-mp-stage__glass';
    stage.append(glass);
    menu.prepend(stage);

    const indicator = document.createElement('span');
    indicator.className = 'autoid-mp-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    nav.append(indicator);

    const scrim = document.createElement('div');
    scrim.className = 'autoid-mp-scrim';
    scrim.setAttribute('aria-hidden', 'true');
    document.body.append(scrim);

    menu.dataset.autoidMpStage = '';

    let openItem = null;
    let hoverItem = null;
    let closeTimer;
    let indicatorItem = null;

    const topItems = () => [...menu.querySelectorAll(TOP_ITEM)];
    const innerOf = (item) => item.querySelector(':scope > .menu-list__submenu--autoid > .menu-list__submenu-inner--autoid');
    const isAutoid = (item) => !!item?.querySelector(':scope > .menu-list__submenu--autoid');
    const nextFrame = (callback) => requestAnimationFrame(() => requestAnimationFrame(callback));

    /* Nothing here reads layout when the menu changes. Horizon's activate() already makes the browser
       recalculate the page once per hover, and a read straight after a change would force a second
       recalculation (measured: 50 to 90ms more per switch with the CPU slowed 4x). Sizes and positions
       are cached from ResizeObservers instead, which report after the browser has laid out anyway. */
    const heights = new Map();
    const positions = new Map();

    /* Stage height follows the open panel. The car panel in its compact layout sizes to the fitment
       widget, which can change height once the app has loaded. */
    function setStageHeight(item) {
      const inner = innerOf(item);
      if (!inner) return;
      if (!heights.has(inner)) heights.set(inner, inner.offsetHeight);
      menu.style.setProperty('--autoid-mp-stage-h', `${heights.get(inner)}px`);
    }
    const panelObserver = new ResizeObserver((entries) => {
      for (const entry of entries) heights.set(entry.target, entry.borderBoxSize[0].blockSize);
      if (openItem) setStageHeight(openItem);
    });
    menu.querySelectorAll('.menu-list__submenu-inner--autoid[data-autoid-chrome="true"]').forEach((inner) => panelObserver.observe(inner));

    function prepareRows(item) {
      const inner = innerOf(item);
      if (!inner || inner.dataset.autoidMpRows) return;
      inner.dataset.autoidMpRows = '';
      for (const selector of STAGGER_ROWS) {
        [...inner.querySelectorAll(selector)].slice(0, STAGGER_MAX).forEach((row, index) => {
          row.dataset.autoidStagger = '';
          row.style.setProperty('--autoid-i', index);
        });
      }
    }

    function markCurrent(scope) {
      const here = location.pathname.replace(/\/$/, '');
      scope.querySelectorAll('a[href]:not([aria-current])').forEach((link) => {
        if (link.pathname.replace(/\/$/, '') === here && link.origin === location.origin) {
          link.setAttribute('aria-current', 'page');
        }
      });
    }

    function open(item) {
      clearTimeout(closeTimer);
      if (item === openItem) return;
      if (!innerOf(item)) return;
      prepareRows(item);
      markCurrent(innerOf(item));

      if (openItem) {
        const items = topItems();
        const direction = items.indexOf(item) > items.indexOf(openItem) ? 1 : -1;
        menu.style.setProperty('--autoid-mp-dir', direction);
        openItem = item;
        setStageHeight(item);
      } else {
        /* A fresh open: the sheet takes the panel's height at once (no ease from the last panel's
           height) and fades and grows in. The height and clip transitions come back on two frames
           later, once the jump has been drawn. */
        menu.style.setProperty('--autoid-mp-dir', 0);
        menu.dataset.autoidMp = 'opening';
        openItem = item;
        setStageHeight(item);
        document.documentElement.classList.add('autoid-mp-open');
        nextFrame(() => {
          if (menu.dataset.autoidMp === 'opening') menu.dataset.autoidMp = 'open';
        });
      }
    }

    function close() {
      if (!openItem) return;
      /* A panel closing for good fades out in place; a switch sets the direction again within the grace. */
      menu.style.setProperty('--autoid-mp-dir', 0);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        openItem = null;
        delete menu.dataset.autoidMp;
        document.documentElement.classList.remove('autoid-mp-open');
      }, CLOSE_GRACE_MS);
    }

    /* The underline sits under the open item, or the hovered one when nothing is open. It slides from
       item to item; when it first appears it grows from the centre where it is, without sliding in. */
    function measureItems() {
      const box = menu.closest('.header__row') || menu.parentElement;
      const boxRect = box.getBoundingClientRect();
      const inset = 12;
      for (const item of topItems()) {
        const link = item.querySelector(':scope > .menu-list__trigger') || item.querySelector(':scope > .menu-list__link');
        if (!link) continue;
        const rect = link.getBoundingClientRect();
        positions.set(item, {
          x: rect.left - boxRect.left - box.clientLeft + inset,
          y: rect.bottom - boxRect.top - box.clientTop,
          width: Math.max(0, rect.width - inset * 2),
        });
      }
    }

    function placeIndicator({ snap = false } = {}) {
      const item = topItems().find((li) => li.querySelector(EXPANDED)) || hoverItem;
      const position = item && positions.get(item);
      if (!position) {
        indicatorItem = null;
        indicator.removeAttribute('data-visible');
        return;
      }
      const fresh = !indicatorItem;
      indicatorItem = item;
      if (fresh || snap) indicator.dataset.snap = '';
      indicator.style.setProperty('--x', `${position.x}px`);
      indicator.style.setProperty('--y', `${position.y}px`);
      indicator.style.setProperty('--w', `${position.width}px`);
      if (fresh) {
        nextFrame(() => {
          delete indicator.dataset.snap;
          if (indicatorItem === item) indicator.dataset.visible = '';
        });
      } else if (snap) {
        nextFrame(() => delete indicator.dataset.snap);
      }
    }

    /* Below 996px nothing counts as open, so a window narrowed with a panel open drops the dim. */
    function sync() {
      const item = DESKTOP.matches ? topItems().find((li) => li.querySelector(EXPANDED)) : null;
      if (item && isAutoid(item)) open(item);
      else close();
      if (DESKTOP.matches) placeIndicator();
    }
    DESKTOP.addEventListener('change', sync);

    new MutationObserver(sync).observe(menu, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
      subtree: true,
    });

    nav.addEventListener('pointerover', (event) => {
      const item = event.target.closest?.(TOP_ITEM);
      if (!item || item === hoverItem) return;
      hoverItem = item;
      if (DESKTOP.matches) placeIndicator();
    });
    nav.addEventListener('pointerleave', () => {
      hoverItem = null;
      placeIndicator();
    });

    /* The items move when the header collapses on scroll, with the window width and when the font loads. */
    const itemObserver = new ResizeObserver(() => {
      measureItems();
      if (indicatorItem) placeIndicator({ snap: true });
    });
    itemObserver.observe(nav);
    topItems().forEach((item) => itemObserver.observe(item));

    /* Panels fetched on demand (assets/autoid-mega-fetch.js) arrive after the panel opened. */
    menu.querySelectorAll('.menu-list__submenu-inner--autoid').forEach((inner) => {
      new MutationObserver(() => markCurrent(inner)).observe(inner, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
